/**
 * ShopShippingDialog - Shipping confirmation dialog for order fulfillment
 * Extracted from AdminShopTab.js in Phase 4D-7
 * 
 * Purpose:
 * - Collect tracking number and URL before marking order as shipped
 * - Indicates that a shipping confirmation email will be sent
 * 
 * Note: Backend handles email notification - this is UI only
 */
import React from 'react';
import { Truck, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

/**
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Object} props.shippingForm - Form state
 * @param {string} props.shippingForm.trackingNumber - Tracking number (optional)
 * @param {string} props.shippingForm.trackingUrl - Tracking URL (optional)
 * @param {Function} props.onFormChange - Callback to update form state
 * @param {Function} props.onConfirm - Callback when Confirm button clicked
 * @param {boolean} props.loading - Whether form is being submitted
 */
export function ShopShippingDialog({
  open,
  onOpenChange,
  shippingForm,
  onFormChange,
  onConfirm,
  loading
}) {
  // Helper to update a single form field
  const updateField = (field, value) => {
    onFormChange(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Mark Order as Shipped
          </DialogTitle>
          <DialogDescription>
            Add tracking details to notify the customer. A shipping confirmation email will be sent automatically.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="tracking-number">Tracking Number (optional)</Label>
            <Input
              id="tracking-number"
              value={shippingForm.trackingNumber}
              onChange={(e) => updateField('trackingNumber', e.target.value)}
              placeholder="e.g., RM123456789GB"
              className="font-mono"
            />
          </div>
          <div>
            <Label htmlFor="tracking-url">Tracking URL (optional)</Label>
            <Input
              id="tracking-url"
              value={shippingForm.trackingUrl}
              onChange={(e) => updateField('trackingUrl', e.target.value)}
              placeholder="https://www.royalmail.com/track/..."
            />
            <p className="text-xs text-slate-500 mt-1">
              Customer will receive a &quot;Track Your Package&quot; button if provided
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Truck className="h-4 w-4 mr-2" />
            Confirm &amp; Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShopShippingDialog;
